import type { ReactNode } from 'react';

import type { IconType } from 'react-icons';

import { type BadgeProps } from './types';
import { COLORS } from '../../types/Colors';
import { cn } from '../../utils/cn';
import { getColorClasses } from '../../utils/Colors';

/**
 * A badge component for displaying labels, tags, or status indicators.
 *
 * @param children - The text content of the badge
 * @param iconLeft - Optional icon to display before the text. Can be an IconType component or ReactNode
 * @param separatorLeft - Optional separator to display between iconLeft and text. Only rendered if iconLeft is provided and text is rendered
 * @param iconRight - Optional icon to display after the text. Can be an IconType component or ReactNode
 * @param separatorRight - Optional separator to display between text and iconRight. Only rendered if iconRight is provided and text is rendered
 * @param color - Color variant of the badge. Maps to colored background, border, and text
 * @param onClick - Click handler for the badge. When provided, the badge becomes clickable
 * @param className - Additional CSS classes
 * @param showTextOnHover - When true and there is text (children), the text is only shown on hover
 *
 * @example
 * <Badge>Partner-Lead</Badge>
 *
 * @example
 * <Badge color={DOT_COLORS.GREEN}>Schufa</Badge>
 *
 * @example
 * <Badge iconLeft={<LuUser />} color={DOT_COLORS.GREEN}>
 *   Partner-Lead
 * </Badge>
 *
 * @example
 * <Badge iconRight={<LuX />} color={DOT_COLORS.GREEN}>
 *   Partner-Lead
 * </Badge>
 *
 * @example
 * <Badge iconLeft={<LuUser />} color={DOT_COLORS.GREEN} />
 *
 * @example
 * <Badge
 *   color={DOT_COLORS.YELLOW}
 *   onClick={() => console.log('clicked')}
 * >
 *   Schufa
 * </Badge>
 *
 * @example
 * <Badge
 *   iconLeft={<LuFileSearch />}
 *   iconRight={<span>A</span>}
 *   separatorRight={<span>•</span>}
 *   color={DOT_COLORS.GREEN}
 *   showTextOnHover={true}
 * >
 *   Schufa
 * </Badge>
 */
export const Badge: React.FC<BadgeProps> = ({
    children,
    className,
    color,
    iconLeft,
    iconRight,
    onClick,
    separatorLeft,
    separatorRight,
    showTextOnHover,
    ...props
}) => {
    const hasIconLeft = iconLeft !== undefined;
    const hasIconRight = iconRight !== undefined;
    const hasIcon = hasIconLeft || hasIconRight;
    const hasText = children !== undefined;
    const isClickable = onClick !== undefined;

    const shouldRenderSeparatorLeft = hasText && hasIconLeft && separatorLeft !== undefined;
    const shouldRenderSeparatorRight = hasText && hasIconRight && separatorRight !== undefined;

    const colorClasses = getColorClasses(color ?? COLORS.GREY);

    const renderIcon = (
        Icon: IconType | ReactNode | undefined,
        testId: string,
        position?: 'left' | 'right',
    ): ReactNode | null => {
        if (!Icon) return null;
        return (
            <span
                className={cn(
                    'shrink-0 size-[14px] flex items-center justify-center',
                    showTextOnHover && position === 'right' && separatorRight && 'group-hover:ml-[5px]',
                    showTextOnHover && position === 'left' && separatorLeft && 'group-hover:mr-[5px]',
                )}
                data-testid={testId}
            >
                {typeof Icon === 'function' ? <Icon className="size-[14px]" /> : Icon}
            </span>
        );
    };

    const iconLeftElement = renderIcon(iconLeft, 'badge-icon-left', 'left');
    const iconRightElement = renderIcon(iconRight, 'badge-icon-right', 'right');

    const isIconOnly = !hasText && hasIcon;
    const shouldApplyGap = (hasText && hasIcon && !showTextOnHover) || (hasIconLeft && hasIconRight);
    const isIconOnlyOrShowTextOnHover = isIconOnly || (showTextOnHover && hasText && hasIcon);
    const hasTwoIconsOnly = hasIconLeft && hasIconRight && (!hasText || (showTextOnHover && hasText));

    const baseClasses = cn(
        'inline-flex items-center',
        shouldApplyGap && !hasTwoIconsOnly && 'gap-[10px]',
        hasTwoIconsOnly && 'gap-[4px]',
        showTextOnHover && hasText && hasIcon && 'group-hover:gap-0',
        isIconOnlyOrShowTextOnHover && 'justify-center',
        showTextOnHover && hasText && hasIcon && 'group-hover:justify-start',
        'font-normal whitespace-nowrap',
        'rounded-[100px] border border-solid',
        'text-sm leading-none',
        'h-[24px]',
        'px-[10px]',
        colorClasses.bg,
        colorClasses.border,
        colorClasses.text,
        'transition-colors',
        isClickable && 'cursor-pointer',
        isClickable && colorClasses.hover,
        showTextOnHover && hasText && 'group',
        className,
    );

    return (
        <div
            className={baseClasses}
            data-testid="badge"
            onClick={isClickable ? onClick : undefined}
            onKeyDown={
                isClickable
                    ? (event) => {
                          if (event.key !== 'Enter' && event.key !== ' ') return;
                          event.preventDefault();
                          event.currentTarget.click();
                      }
                    : undefined
            }
            role={isClickable ? 'button' : undefined}
            tabIndex={isClickable ? 0 : undefined}
            {...props}
        >
            {iconLeftElement}
            {shouldRenderSeparatorLeft && (
                <span
                    className={cn(
                        'shrink-0 -mx-[6px]',
                        showTextOnHover &&
                            'inline-flex opacity-0 max-w-0 overflow-hidden group-hover:opacity-100 group-hover:max-w-[500px] transition-all duration-300 ease-in-out leading-none',
                    )}
                    data-testid="badge-separator-left"
                >
                    {separatorLeft}
                </span>
            )}
            {hasText && (
                <span
                    className={cn(
                        showTextOnHover &&
                            'opacity-0 max-w-0 overflow-hidden group-hover:opacity-100 group-hover:max-w-[500px] transition-all duration-300 ease-in-out leading-none',
                        showTextOnHover && hasIconLeft && 'group-hover:ml-[5px]',
                        showTextOnHover && hasIconRight && 'group-hover:mr-[5px]',
                    )}
                >
                    {children}
                </span>
            )}
            {shouldRenderSeparatorRight && (
                <span
                    className={cn(
                        'shrink-0 -mx-[6px]',
                        showTextOnHover &&
                            'inline-flex opacity-0 max-w-0 overflow-hidden group-hover:opacity-100 group-hover:max-w-[500px] transition-all duration-300 ease-in-out leading-none',
                    )}
                    data-testid="badge-separator-right"
                >
                    {separatorRight}
                </span>
            )}
            {iconRightElement}
        </div>
    );
};

Badge.displayName = 'Badge';
